import React, { createContext, useCallback, useContext, useMemo, useRef } from "react";
import * as Y from "yjs";
import type { YjsSignalrConnectorE2EE } from "../impl/yjsSignalrConnectorE2EE";
import type { SenderKeyState, RemoteSenderKeyState, EncryptedYjsUpdate } from "../crypto/utils/yjsUpdateEncrypt";
import { makeRoomId, RoomId, RoomParticipant } from "../crypto/types/roomKeys";

type FilePath = string;
type UserId = string;
type SenderKeyId = string;

// Local sender key tracked per room; aligns with SenderKeyState but can hold optional pub keys
export type LocalKeyState = SenderKeyState & {
  rootChainKey: Uint8Array; // ✨ Original chain key for snapshots (never ratchets)
  ed25519PublicKey?: Uint8Array;
  x25519PublicKey?: Uint8Array;
  x25519PrivateKey?: Uint8Array;
};

// Remote sender key tracked per room; aligns with RemoteSenderKeyState
export type RemoteKeyState = RemoteSenderKeyState & {
  rootChainKey?: Uint8Array; // ✨ Original chain key for snapshots
  x25519PublicKey?: Uint8Array;
};

// Incoming encrypted payload type (matches backend DTO shape) with optional kind flag
export type EncryptedWire = EncryptedYjsUpdate & {
  kind?: "update" | "snapshot";
};

export type FileRoomState = {
  roomId: RoomId;
  sessionId: string;
  filePath: FilePath;

  users: Map<UserId, RoomParticipant>;

  local?: LocalKeyState;

  // remote ring: senderUserId -> senderKeyId -> RemoteKeyState
  remotes: Map<UserId, Map<SenderKeyId, RemoteKeyState>>;

  // buffer: senderUserId -> senderKeyId -> EncryptedWire[]
  pending: Map<UserId, Map<SenderKeyId, EncryptedWire[]>>;
};

export type FileRoomKeyStore = {
  // room
  getOrCreateRoom: (sessionId: string, filePath: string) => FileRoomState;
  removeRoom: (sessionId: string, filePath: string) => void;

  // Ensure local key exists (fresh per join). Returns the stored key.
  ensureLocalKey: (
    sessionId: string,
    filePath: string,
    userId: string,
    ed25519PrivateKey: Uint8Array,
    opts?: {
      ed25519PublicKey?: Uint8Array;
      x25519PrivateKey?: Uint8Array;
      x25519PublicKey?: Uint8Array;
    }
  ) => LocalKeyState;

  // users (optional)
  upsertUser: (sessionId: string, filePath: string, user: RoomParticipant) => void;
  removeUser: (sessionId: string, filePath: string, userId: string) => void;

  // local key
  getLocalKey: (sessionId: string, filePath: string) => LocalKeyState | undefined;
  setLocalKey: (sessionId: string, filePath: string, local: LocalKeyState) => void;

  // remote key ring
  upsertRemoteKey: (
    sessionId: string,
    filePath: string,
    senderUserId: string,
    key: RemoteKeyState
  ) => void;

  getRemoteKey: (
    sessionId: string,
    filePath: string,
    senderUserId: string,
    senderKeyId: string
  ) => RemoteKeyState | undefined;

  // buffering
  bufferWire: (sessionId: string, filePath: string, wire: EncryptedWire) => void;
  drainBuffered: (sessionId: string, filePath: string, senderUserId: string, senderKeyId: string) => EncryptedWire[];
};

type DocumentApi = {
  /** Get existing connector for a virtual filePath */
  getConnector: (filePath: string) => YjsSignalrConnectorE2EE | undefined;

  /** Get existing Y.Doc for a virtual filePath */
  getDoc: (filePath: string) => Y.Doc | undefined;

  // NEW (owner entry point)
  getOrCreateConnector: (
    filePath: string,
    factory: () => YjsSignalrConnectorE2EE
  ) => YjsSignalrConnectorE2EE;

  // NEW: Get or create Y.Doc for a file
  getOrCreateDoc: (filePath: string, factory: () => Y.Doc) => Y.Doc;

  /** Register/replace connector for a virtual filePath */
  setConnector: (filePath: string, connector: YjsSignalrConnectorE2EE) => void;

  /** Remove connector from registry without disposing */
  removeConnector: (filePath: string) => void;

  /** Remove doc from registry without disposing */
  removeDoc: (filePath: string) => void;

  /** Dispose (if supported) and remove connector */
  disposeConnector: (filePath: string) => void;

  /** Dispose Y.Doc (destroy) and remove */
  disposeDoc: (filePath: string) => void;

  /** Dispose all connectors (e.g., on session end) */
  disposeAll: () => void;

  keyStore: FileRoomKeyStore;
  
};

const YjsDocumentContext = createContext<DocumentApi | null>(null);

export function useYjsDocument(): DocumentApi {
  const ctx = useContext(YjsDocumentContext);
  if (!ctx) {
    throw new Error("useYjsDocument must be used within YjsDocumentProvider");
  }
  return ctx;
}

export const YjsDocumentProvider = ({ children }: { children: React.ReactNode }) => {
  const connectorsRef = useRef<Map<string, YjsSignalrConnectorE2EE>>(new Map());
  const docsRef = useRef<Map<string, Y.Doc>>(new Map());

  const fileRoomsRef = useRef<Map<string, FileRoomState>>(new Map());

  const createSenderKeyId = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

  const createChainKey = () => {
    const buf = new Uint8Array(32);
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
      crypto.getRandomValues(buf);
    } else {
      for (let i = 0; i < buf.length; i++) {
        buf[i] = Math.floor(Math.random() * 256);
      }
    }
    return buf;
  };

  const keyStore: FileRoomKeyStore = {
    getOrCreateRoom: (sessionId, filePath) => {
      const k = makeRoomId(sessionId, filePath);
      let room = fileRoomsRef.current.get(k);
      if (!room) {
        room = {
          roomId: k,
          sessionId,
          filePath,
          users: new Map(),
          remotes: new Map(),
          pending: new Map(),
        };
        fileRoomsRef.current.set(k, room);
      }
      return room;
    },

    removeRoom: (sessionId, filePath) => {
      fileRoomsRef.current.delete(makeRoomId(sessionId, filePath));
    },

    ensureLocalKey: (sessionId, filePath, userId, ed25519PrivateKey, opts) => {
      const room = keyStore.getOrCreateRoom(sessionId, filePath);
      if (room.local) return room.local;

      const senderKeyId = createSenderKeyId();
      const chainKey = createChainKey();

      const local: LocalKeyState = {
        sessionId,
        filePath,
        userId,
        senderKeyId,
        chainKey,
        rootChainKey: new Uint8Array(chainKey), // ✨ Store root chain key for snapshots
        nextMessageIndex: 1, // ✨ Reserve 0 for snapshot; updates start at 1
        ed25519PrivateKey: new Uint8Array(ed25519PrivateKey),
        ed25519PublicKey: opts?.ed25519PublicKey ? new Uint8Array(opts.ed25519PublicKey) : undefined,
        x25519PrivateKey: opts?.x25519PrivateKey ? new Uint8Array(opts.x25519PrivateKey) : undefined,
        x25519PublicKey: opts?.x25519PublicKey ? new Uint8Array(opts.x25519PublicKey) : undefined,
      };

      room.local = local;
      return local;
    },

    upsertUser: (sessionId, filePath, user) => {
      const room = keyStore.getOrCreateRoom(sessionId, filePath);
      room.users.set(user.userId, user);
    },

    removeUser: (sessionId, filePath, userId) => {
      const room = fileRoomsRef.current.get(makeRoomId(sessionId, filePath));
      if (!room) return;
      room.users.delete(userId);
      // optional: also remove remote keys/pending for that user
      room.remotes.delete(userId);
      room.pending.delete(userId);
    },
  
    getLocalKey: (sessionId, filePath) => {
      return fileRoomsRef.current.get(makeRoomId(sessionId, filePath))?.local;
    },
  
    setLocalKey: (sessionId, filePath, local) => {
      const room = keyStore.getOrCreateRoom(sessionId, filePath);

      // Store copies to avoid accidental shared references
      room.local = {
        sessionId,
        filePath,
        userId: local.userId,
        senderKeyId: local.senderKeyId,
        chainKey: new Uint8Array(local.chainKey),
        rootChainKey: new Uint8Array(local.rootChainKey), // ✨ Preserve root chain key
        nextMessageIndex: local.nextMessageIndex,
        ed25519PrivateKey: new Uint8Array(local.ed25519PrivateKey),
        ed25519PublicKey: local.ed25519PublicKey ? new Uint8Array(local.ed25519PublicKey) : undefined,
        x25519PrivateKey: local.x25519PrivateKey ? new Uint8Array(local.x25519PrivateKey) : undefined,
        x25519PublicKey: local.x25519PublicKey ? new Uint8Array(local.x25519PublicKey) : undefined,
      };
    },

    upsertRemoteKey: (sessionId, filePath, senderUserId, key) => {
      const room = keyStore.getOrCreateRoom(sessionId, filePath);

      let bySenderKeyId = room.remotes.get(senderUserId);
      if (!bySenderKeyId) {
        bySenderKeyId = new Map();
        room.remotes.set(senderUserId, bySenderKeyId);
      }

      bySenderKeyId.set(key.senderKeyId, {
        sessionId,
        filePath,
        senderUserId,
        senderKeyId: key.senderKeyId,
        chainKey: new Uint8Array(key.chainKey),
        rootChainKey: key.rootChainKey ? new Uint8Array(key.rootChainKey) : undefined,
        lastMessageIndex: key.lastMessageIndex,
        ed25519PublicKey: new Uint8Array(key.ed25519PublicKey),
        x25519PublicKey: key.x25519PublicKey ? new Uint8Array(key.x25519PublicKey) : undefined,
      });
    },

    getRemoteKey: (sessionId, filePath, senderUserId, senderKeyId) => {
      const room = fileRoomsRef.current.get(makeRoomId(sessionId, filePath));
      return room?.remotes.get(senderUserId)?.get(senderKeyId);
    },

    bufferWire: (sessionId, filePath, wire) => {
      const room = keyStore.getOrCreateRoom(sessionId, filePath);
      const senderUserId = wire.userId;
      const senderKeyId = wire.senderKeyId;

      let bySenderKeyId = room.pending.get(senderUserId);
      if (!bySenderKeyId) {
        bySenderKeyId = new Map();
        room.pending.set(senderUserId, bySenderKeyId);
      }

      const q = bySenderKeyId.get(senderKeyId) ?? [];
      q.push(wire);
      bySenderKeyId.set(senderKeyId, q);
    },

    drainBuffered: (sessionId, filePath, senderUserId, senderKeyId) => {
      const room = fileRoomsRef.current.get(makeRoomId(sessionId, filePath));
      if (!room) return [];

      const bySenderKeyId = room.pending.get(senderUserId);
      if (!bySenderKeyId) return [];

      const q = bySenderKeyId.get(senderKeyId) ?? [];
      bySenderKeyId.delete(senderKeyId);

      if (bySenderKeyId.size === 0) room.pending.delete(senderUserId);
      return q;
    },
  };

  const getConnector = useCallback((filePath: string) => {
    return connectorsRef.current.get(filePath);
  }, []);

  const getDoc = useCallback((filePath: string) => {
    return docsRef.current.get(filePath);
  }, []);

  const getOrCreateConnector = useCallback((filePath: string, factory: () => any) => {
    const existing = connectorsRef.current.get(filePath);
    if (existing) return existing;

    const connector = factory();
    connectorsRef.current.set(filePath, connector);
    return connector;
  }, []);

  const api = useMemo<DocumentApi>(() => {
    return {
      getConnector,
      getDoc,
      getOrCreateConnector,
      keyStore,
      
      getOrCreateDoc(filePath, factory) {
        const existing = docsRef.current.get(filePath);
        if (existing) return existing;
        const doc = factory();
        docsRef.current.set(filePath, doc);
        return doc;
      },

      setConnector(filePath, connector) {
        connectorsRef.current.set(filePath, connector);
      },

      removeConnector(filePath) {
        connectorsRef.current.delete(filePath);
      },

      removeDoc(filePath) {
        docsRef.current.delete(filePath);
      },

      disposeConnector(filePath: string) {
        const conn = connectorsRef.current.get(filePath);
        if (!conn) return;

        try {
          conn.destroy();
        } catch (e) {
          console.error("[FAIL][Document] disposeConnector failed:", e);
        } finally {
          connectorsRef.current.delete(filePath);
        }
      },

      disposeDoc(filePath: string) {
        const doc = docsRef.current.get(filePath);
        if (!doc) return;
        try {
          doc.destroy();
        } catch (e) {
          console.error("[FAIL][Document] disposeDoc failed:", e);
        } finally {
          docsRef.current.delete(filePath);
        }
      },

      disposeAll() {
        connectorsRef.current.forEach((conn, filePath) => {
          try {
            conn.destroy();
          } catch (e) {
            console.error(`[FAIL][Document] disposeAll failed for ${filePath}:`, e);
          }
        });

        connectorsRef.current.clear();

        docsRef.current.forEach((doc, filePath) => {
          try {
            doc.destroy();
          } catch (e) {
            console.error(`[FAIL][Document] disposeAll (doc) failed for ${filePath}:`, e);
          }
        });
        docsRef.current.clear();
      }
    };
  }, []);

  return (
    <YjsDocumentContext.Provider value={api}>
      {children}
    </YjsDocumentContext.Provider>
  );
}